// ─── El protocolo entre el editor y el iframe sandbox de una animación ───────
// El código del agente corre en un iframe con `sandbox="allow-scripts"` (origen
// opaco: sin la sesión de Supabase, sin storage, sin la ventana de arriba). Lo
// único que cruza esa frontera son estos mensajes por `postMessage`, en los dos
// sentidos. Puro: tipos y guardas, lo importan el editor (`SandboxAnimacion`) y
// la página del sandbox (`src/sandbox/animacion.tsx`).

import type { AssetDeAnimacion } from '../codigo/versiones';

/** Marca de todos los mensajes, para no confundirlos con los de otros (OAuth, service worker). */
export const CANAL = 'atlas-animacion-sandbox' as const;

/** Lo que el código recibe por props. La ÚNICA forma de pasarle datos. */
export type PropsDeAnimacion = {
  assets: AssetDeAnimacion[];
  marca: { colores: string[]; fuentes: string[]; nombre: string };
  duracionEnFrames: number;
};

/** Editor → sandbox. */
export type MensajeAlSandbox =
  | {
      canal: typeof CANAL;
      tipo: 'montar';
      clipId: string;
      codigo: string;
      props: PropsDeAnimacion;
      fps: number;
      duracionEnFrames: number;
      ancho: number;
      alto: number;
    }
  | { canal: typeof CANAL; tipo: 'ir_a'; clipId: string; frame: number }
  | {
      canal: typeof CANAL;
      tipo: 'capturar';
      clipId: string;
      frames: number[];
      anchoPx: number;
      /** JPEG chico (miniaturas para guardar) en vez de PNG (para que el agente mire). */
      jpeg?: boolean;
    };

/** Sandbox → editor. */
export type MensajeDelSandbox =
  | { canal: typeof CANAL; tipo: 'cargado' }
  | { canal: typeof CANAL; tipo: 'listo'; clipId: string }
  | { canal: typeof CANAL; tipo: 'error'; clipId: string; mensaje: string; etapa: 'lint' | 'sintaxis' | 'ejecucion' | 'render' }
  | { canal: typeof CANAL; tipo: 'capturas'; clipId: string; imagenes: string[] };

const esObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** ¿Es un mensaje nuestro (del sandbox al editor)? Cualquier otra cosa se ignora. */
export function esMensajeDelSandbox(data: unknown): data is MensajeDelSandbox {
  if (!esObjeto(data) || data.canal !== CANAL || typeof data.tipo !== 'string') return false;
  switch (data.tipo) {
    case 'cargado':
      return true;
    case 'listo':
      return typeof data.clipId === 'string';
    case 'error':
      return typeof data.clipId === 'string' && typeof data.mensaje === 'string';
    case 'capturas':
      return (
        typeof data.clipId === 'string' &&
        Array.isArray(data.imagenes) &&
        data.imagenes.every((i) => typeof i === 'string')
      );
    default:
      return false;
  }
}

/** ¿Es un mensaje nuestro (del editor al sandbox)? */
export function esMensajeAlSandbox(data: unknown): data is MensajeAlSandbox {
  if (!esObjeto(data) || data.canal !== CANAL || typeof data.tipo !== 'string') return false;
  if (typeof data.clipId !== 'string') return false;
  switch (data.tipo) {
    case 'montar':
      return typeof data.codigo === 'string' && esObjeto(data.props) && typeof data.fps === 'number';
    case 'ir_a':
      return typeof data.frame === 'number';
    case 'capturar':
      return Array.isArray(data.frames) && typeof data.anchoPx === 'number';
    default:
      return false;
  }
}
